import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Layout } from "./components/Layout";
import { PortalLayout } from "./components/PortalLayout";
import { Toaster } from "./components/ui/sonner";
import { RolePreviewBanner } from "./components/RolePreviewBanner";
import { ImpersonationBanner } from "./components/impersonation/ImpersonationBanner";
import { ReadOnlyGuard } from "./components/impersonation/ReadOnlyGuard";
import { InstallIdentity } from "./components/InstallIdentity";

// Pages
import { Login } from "./pages/Login";
import { ProjectsList } from "./pages/ProjectsList";
import { CreateProject } from "./pages/CreateProject";
import { ProjectDetail } from "./pages/ProjectDetail";
import { Settings } from "./pages/Settings";
import { Schedule } from "./pages/Schedule";
import { TendersList } from "./pages/TendersList";
import { CreateTender } from "./pages/CreateTender";
import { TenderDetail } from "./pages/TenderDetail";
import { ContactsList } from "./pages/ContactsList";

// Portal Pages
import { PortalHome } from "./pages/portal/PortalHome";

// Admin Pages
import CapturesAdmin from "./pages/admin/CapturesAdmin";
import Capture from "./pages/Capture";
import AllCaptures from "./pages/AllCaptures";
import ScheduleChanges from "./pages/ScheduleChanges";
import DiaryAudit from "./pages/DiaryAudit";
import CostLedger from "./pages/CostLedger";
import MatchReview from "./pages/MatchReview";
import CostInbox from "./pages/CostInbox";
import CostInboxSends from "./pages/CostInboxSends";
import CostInboxEmailIn from "./pages/CostInboxEmailIn";
import ClientPOs from "./pages/ClientPOs";
import { PortalSchedule } from "./pages/portal/PortalSchedule";
import { PortalJobDetail } from "./pages/portal/PortalJobDetail";
import { PortalSiteNotes } from "./pages/portal/PortalSiteNotes";
import { PortalSiteNoteCreate } from "./pages/portal/PortalSiteNoteCreate";
import { PortalOrders } from "./pages/portal/PortalOrders";
import { PortalOrderCreate } from "./pages/portal/PortalOrderCreate";
import { PortalOrderDetail } from "./pages/portal/PortalOrderDetail";
import PurchaseOrders from "./pages/PurchaseOrders";
import CisLedger from "./pages/CisLedger";
import Business from "./pages/business/Business";
import PlanPrint from "./pages/business/PlanPrint";
import { PortalSurveys } from "./pages/portal/PortalSurveys";
import { PortalProfile } from "./pages/portal/PortalProfile";
import { PortalTeamSchedule } from "./pages/portal/PortalTeamSchedule";
import { PortalNewProject } from "./pages/portal/PortalNewProject";
import PortalInvoice from "./pages/portal/PortalInvoice";
import PortalInvoiceCreate from "./pages/portal/PortalInvoiceCreate";
import PortalInvoiceDetail from "./pages/portal/PortalInvoiceDetail";
import { MyCaptures } from "./pages/portal/MyCaptures";
import { MyCaptureDetail } from "./pages/portal/MyCaptureDetail";
import { BuildingControl } from "./pages/portal/BuildingControl";
import { BuildingControlDetail } from "./pages/portal/BuildingControlDetail";
import { ProjectPicker } from "./pages/diary/ProjectPicker";
import { DayView } from "./pages/diary/DayView";
import { ProjectCostLog } from "./pages/diary/ProjectCostLog";
import AuthCallback from "./pages/AuthCallback";

// Where to send someone back to after they log in (a Diary PWA launched
// at /diary must land in the diary, not the ops home).
function toLogin(location) {
  return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
}

// Protected Route Component - for desktop app (restricted from portal-only users)
function ProtectedRoute({ children, bare = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1E2530]">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!user) {
    return toLogin(location);
  }
  
  // Portal-only users cannot access the desktop app. This now includes
  // SURVEYORS as well as employees + sub-contractors (OA-1 refinement):
  // surveyors get the same restricted portal shell on desktop as on mobile
  // (Ops Portal + Site Diary hub tiles + Capture/Schedule/Projects/Surveys/
  // New Project). Direct URL navigation to /projects, /tenders, /contacts,
  // /schedule, /captures, /settings etc. is bounced here.
  const portalOnlyTypes = ['employee', 'operative', 'sub_contractor', 'surveyor'];
  if (portalOnlyTypes.includes(user.user_type)) {
    return <Navigate to="/portal" replace />;
  }

  // Role-first (Ops-App brief): admins ALWAYS get the admin app — phone,
  // tablet and desktop. The admin Layout is responsive (hamburger + bottom
  // nav on phone), so there is no device-based or user-chosen switch to the
  // operative portal.
  return bare ? children : <Layout>{children}</Layout>;
}

// Portal tools the admin app deliberately hands off to (Site Diary tiles →
// Capture / Building Control). Admins may use these; the rest of the portal
// (home, schedule, invoices…) still bounces them to the admin app.
const ADMIN_PORTAL_TOOLS = ['/portal/capture', '/portal/my-captures', '/portal/building-control'];

// Portal Protected Route - for employees and sub-contractors
function PortalRoute({ children }) {
  const { user, loading, isImpersonating } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1E2530]">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!user) {
    return toLogin(location);
  }

  // Role-first (Ops-App brief): a real admin never lands in the operative
  // portal — bounce them to the admin app. Exceptions: while previewing another
  // role the admin SHOULD see that role's portal, and the shared tool pages
  // the diary links into are fine.
  const isTool = ADMIN_PORTAL_TOOLS.some((p) => location.pathname.startsWith(p));
  if (user.user_type === 'admin' && !isImpersonating && !isTool) {
    return <Navigate to="/projects" replace />;
  }
  
  return <PortalLayout>{children}</PortalLayout>;
}

// Cost Ledger Route — admin + surveyor only. Surveyors are otherwise
// portal-locked; the ledger is an exception (surveyor is read-only).
// Wraps in Layout for admins so the standard sidebar renders; surveyors
// see the page standalone (they have no desktop nav).
function CostLedgerRoute({ children }) {
  const { user, loading, isAdmin, isSurveyor } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1E2530]">
        <div className="spinner" />
      </div>
    );
  }
  if (!user) return toLogin(location);
  if (!isAdmin && !isSurveyor) return <Navigate to="/portal" replace />;
  if (isAdmin) return <Layout>{children}</Layout>;
  return children;
}

// Site Diary Route — admins and surveyors only. Everyone else gets bounced
// to their normal landing. The DiaryLayout itself hides the ops-app toggle
// from surveyors, so the "surveyor locked to diary" rule is enforced both
// by route AND by UI.
function DiaryRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1E2530]">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) return toLogin(location);

  const allowed = user.user_type === 'admin' || user.user_type === 'surveyor';
  if (!allowed) return <Navigate to="/portal" replace />;

  return children;
}

// Public Route Component (redirects to appropriate page if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1E2530]">
        <div className="spinner" />
      </div>
    );
  }
  
  if (user) {
    // Back to wherever the guard sent us here from (e.g. the Diary PWA's
    // /diary start URL); otherwise the role's normal landing.
    const from = location.state?.from;
    if (from && from !== '/login') return <Navigate to={from} replace />;
    // OA-1: everyone starts at /portal after login except desktop admins.
    // Surveyors + employees + sub-contractors always go to /portal.
    if (['surveyor', 'employee', 'sub_contractor'].includes(user.user_type)) {
      return <Navigate to="/portal" replace />;
    }
    return <Navigate to="/projects" replace />;
  }
  
  return children;
}

function AppRoutes() {
  // Render the AuthCallback ABOVE the route table when the URL fragment
  // contains a session_id — this wins the race against ProtectedRoute /
  // PublicRoute auth checks that would otherwise fire on /login first.
  if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />

      {/* Google sign-in landing page (also reached without hash for direct visits) */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Protected Routes */}
      <Route path="/projects" element={
        <ProtectedRoute>
          <ProjectsList />
        </ProtectedRoute>
      } />
      
      <Route path="/projects/new" element={
        <ProtectedRoute>
          <CreateProject />
        </ProtectedRoute>
      } />
      
      <Route path="/projects/:projectId" element={
        <ProtectedRoute>
          <ProjectDetail />
        </ProtectedRoute>
      } />
      
      <Route path="/schedule" element={
        <ProtectedRoute>
          <Schedule />
        </ProtectedRoute>
      } />
      
      <Route path="/tenders" element={
        <ProtectedRoute>
          <TendersList />
        </ProtectedRoute>
      } />
      
      <Route path="/tenders/new" element={
        <ProtectedRoute>
          <CreateTender />
        </ProtectedRoute>
      } />
      
      <Route path="/tenders/:tenderId" element={
        <ProtectedRoute>
          <TenderDetail />
        </ProtectedRoute>
      } />
      
      <Route path="/contacts" element={
        <ProtectedRoute>
          <ContactsList />
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />

      {/* Site Diary — admins & surveyors only. Two shortcuts (deep links):
              /diary            → project picker (landing)
              /diary/:projectId → day view for that project (today)
          Both share the same code path, just different URLs. */}
      <Route path="/diary" element={
        <DiaryRoute>
          <ProjectPicker />
        </DiaryRoute>
      } />
      <Route path="/diary/:projectId" element={
        <DiaryRoute>
          <DayView />
        </DiaryRoute>
      } />
      <Route path="/diary/:projectId/costs" element={
        <DiaryRoute>
          <ProjectCostLog />
        </DiaryRoute>
      } />

      {/* DIA-1 §2.6 — /portal/diary/* is the portal-shell diary. URL prefix
          drives the X-View-Context header (auto-attached in AuthContext),
          which is what the diary audit log uses to distinguish portal vs
          dashboard actions. Same components; the URL is the discriminator. */}
      <Route path="/portal/diary" element={
        <DiaryRoute>
          <ProjectPicker />
        </DiaryRoute>
      } />
      <Route path="/portal/diary/:projectId" element={
        <DiaryRoute>
          <DayView />
        </DiaryRoute>
      } />
      <Route path="/portal/diary/:projectId/costs" element={
        <DiaryRoute>
          <ProjectCostLog />
        </DiaryRoute>
      } />
      
      <Route path="/admin/captures" element={
        <ProtectedRoute>
          <CapturesAdmin />
        </ProtectedRoute>
      } />
      
      {/* Legacy /capture redirects to unified /captures */}
      <Route path="/capture" element={<Navigate to="/captures" replace />} />

      <Route path="/captures" element={
        <ProtectedRoute>
          <Capture />
        </ProtectedRoute>
      } />

      <Route path="/captures/history" element={
        <ProtectedRoute>
          <AllCaptures />
        </ProtectedRoute>
      } />
      
      <Route path="/schedule-changes" element={
        <ProtectedRoute>
          <ScheduleChanges />
        </ProtectedRoute>
      } />

      {/* DIA-2 §2.4 — Diary Audit Report, admin desktop only. */}
      <Route path="/diary-audit" element={
        <ProtectedRoute>
          <DiaryAudit />
        </ProtectedRoute>
      } />

      {/* L-2 Group 1 — Desktop Master Cost Ledger, admin + surveyor. */}
      <Route path="/cost-ledger" element={
        <CostLedgerRoute>
          <CostLedger />
        </CostLedgerRoute>
      } />

      {/* Provisional capture ↔ invoice match review, admin only (page self-guards). */}
      <Route path="/cost-ledger/matches" element={
        <CostLedgerRoute>
          <MatchReview />
        </CostLedgerRoute>
      } />

      {/* L-3 — Cost Inbox, admin only (page self-guards non-admins). */}
      <Route path="/cost-inbox" element={
        <ProtectedRoute>
          <CostInbox />
        </ProtectedRoute>
      } />
      <Route path="/cost-inbox/sends" element={
        <ProtectedRoute>
          <CostInboxSends />
        </ProtectedRoute>
      } />
      <Route path="/cost-inbox/email-in" element={
        <ProtectedRoute>
          <CostInboxEmailIn />
        </ProtectedRoute>
      } />
      <Route path="/client-pos" element={
        <ProtectedRoute>
          <ClientPOs />
        </ProtectedRoute>
      } />
      <Route path="/purchase-orders" element={
        <ProtectedRoute>
          <PurchaseOrders />
        </ProtectedRoute>
      } />
      <Route path="/cis-ledger" element={
        <ProtectedRoute>
          <CisLedger />
        </ProtectedRoute>
      } />
      {/* Business — goals & initiatives that move the company forwards, admin only. */}
      <Route path="/business" element={
        <ProtectedRoute>
          <Business />
        </ProtectedRoute>
      } />
      <Route path="/business/print" element={
        <ProtectedRoute bare>
          <PlanPrint />
        </ProtectedRoute>
      } />
      
      {/* Portal Routes (for employees and sub-contractors) */}
      <Route path="/portal" element={
        <PortalRoute>
          <PortalHome />
        </PortalRoute>
      } />
      
      <Route path="/portal/schedule" element={
        <PortalRoute>
          <PortalSchedule />
        </PortalRoute>
      } />

      <Route path="/portal/job/:projectId" element={
        <PortalRoute>
          <PortalJobDetail />
        </PortalRoute>
      } />
      
      <Route path="/portal/surveys" element={
        <PortalRoute>
          <PortalSurveys />
        </PortalRoute>
      } />
      
      <Route path="/portal/profile" element={
        <PortalRoute>
          <PortalProfile />
        </PortalRoute>
      } />
      
      {/* Surveyor-specific routes */}
      <Route path="/portal/team-schedule" element={
        <PortalRoute>
          <PortalTeamSchedule />
        </PortalRoute>
      } />
      
      <Route path="/portal/capture" element={
        <PortalRoute>
          <Capture />
        </PortalRoute>
      } />
      
      <Route path="/portal/my-captures" element={
        <PortalRoute>
          <MyCaptures />
        </PortalRoute>
      } />
      
      <Route path="/portal/my-captures/:captureId" element={
        <PortalRoute>
          <MyCaptureDetail />
        </PortalRoute>
      } />

      <Route path="/portal/building-control" element={
        <PortalRoute>
          <BuildingControl />
        </PortalRoute>
      } />

      <Route path="/portal/building-control/:captureId" element={
        <PortalRoute>
          <BuildingControlDetail />
        </PortalRoute>
      } />
      
      <Route path="/portal/new-project" element={
        <PortalRoute>
          <PortalNewProject />
        </PortalRoute>
      } />

      {/* OA-1 §Section 4 — Invoice tab */}
      <Route path="/portal/invoice" element={
        <PortalRoute>
          <PortalInvoice />
        </PortalRoute>
      } />
      <Route path="/portal/invoice/create" element={
        <PortalRoute>
          <PortalInvoiceCreate />
        </PortalRoute>
      } />
      <Route path="/portal/invoice/:invoiceId/edit" element={
        <PortalRoute>
          <PortalInvoiceCreate />
        </PortalRoute>
      } />
      <Route path="/portal/invoice/:invoiceId" element={
        <PortalRoute>
          <PortalInvoiceDetail />
        </PortalRoute>
      } />
      <Route path="/portal/site-notes" element={
        <PortalRoute>
          <PortalSiteNotes />
        </PortalRoute>
      } />
      <Route path="/portal/site-notes/new" element={
        <PortalRoute>
          <PortalSiteNoteCreate />
        </PortalRoute>
      } />
      <Route path="/portal/site-notes/:noteId/edit" element={
        <PortalRoute>
          <PortalSiteNoteCreate />
        </PortalRoute>
      } />
      <Route path="/portal/orders" element={
        <PortalRoute>
          <PortalOrders />
        </PortalRoute>
      } />
      <Route path="/portal/orders/new" element={
        <PortalRoute>
          <PortalOrderCreate />
        </PortalRoute>
      } />
      <Route path="/portal/orders/:orderId" element={
        <PortalRoute>
          <PortalOrderDetail />
        </PortalRoute>
      } />
      
      {/* Redirect root to projects or login */}
      <Route path="/" element={<Navigate to="/projects" replace />} />
      
      {/* Catch all - redirect to projects */}
      <Route path="*" element={<Navigate to="/projects" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <InstallIdentity />
        <ImpersonationBanner />
        <ReadOnlyGuard />
        <RolePreviewBanner />
        <AppRoutes />
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: '#2A3240',
              color: '#FFFFFF',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
